import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';

import { useApi } from '@/lib/api-context';

import { inboxPageSchema, markAllReadSchema, type InboxItem } from './types';

export { useUnreadCount } from '@/hooks/useUnreadNotifications';

// Bandeja de avisos del usuario (so=370, backend so=330). La empresa activa va en la
// posición 1 del queryKey: nunca se sirve la caché de otra empresa.

export const INBOX_PAGE_SIZE = 20;
export type InboxFilter = 'abiertos' | 'todos';

interface Scope {
  companyId: string | null;
  enabled: boolean;
}

export interface InboxPage {
  items: InboxItem[];
  nextCursor: string | null;
}

export function useInbox(
  filter: InboxFilter,
  { companyId, enabled }: Scope,
): UseInfiniteQueryResult<InfiniteData<InboxPage, string | null>> {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: ['notifications', companyId, filter],
    queryFn: async ({ pageParam }) => {
      const status = filter === 'todos' ? 'all' : 'open';
      const cursor = pageParam === null ? '' : `&cursor=${encodeURIComponent(pageParam)}`;
      const raw = await api.get<unknown>(
        `/api/v1/me/notifications?status=${status}&limit=${INBOX_PAGE_SIZE}${cursor}`,
      );
      const parsed = inboxPageSchema.parse(raw);
      return { items: parsed.data, nextCursor: parsed.meta.next_cursor };
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
}

function useInboxAction(action: 'read' | 'dismiss'): UseMutationResult<unknown, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch<unknown>(`/api/v1/me/notifications/${id}/${action}`, {}),
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['notifications'] }),
        qc.invalidateQueries({ queryKey: ['notifications-count'] }),
      ]);
    },
  });
}

export function useMarkRead(): UseMutationResult<unknown, Error, string> {
  return useInboxAction('read');
}

export function useDismiss(): UseMutationResult<unknown, Error, string> {
  return useInboxAction('dismiss');
}

/** Marca leídos todos los avisos sin leer de la empresa activa; devuelve cuántos. */
export function useMarkAllRead(): UseMutationResult<number, Error, void> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      markAllReadSchema.parse(await api.patch<unknown>('/api/v1/me/notifications/read-all', {}))
        .data.updated,
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['notifications'] }),
        qc.invalidateQueries({ queryKey: ['notifications-count'] }),
      ]);
    },
  });
}
